import useComponentBlock from '@components/component-block/component-block.hook';
import Container from '@components/container';
import Img from '@components/img';
import type { SanityImageSource } from '@sanity/image-url/lib/types/types';
import classNames from 'classnames';
import type { CSSProperties, PropsWithChildren, ReactElement } from 'react';
import React from 'react';
import type { Themable } from 'types/components/fragments/themeable';
import type { ComponentSizes, Sizable } from 'types/props/sizable';
import { validateString } from 'utils/validate-string';

import styles from './component-block.module.scss';

const contentClass = validateString(styles.content);
const fullscreenClass = validateString(styles.fullscreen);
const backgroundImageClass = validateString(styles.backgroundImage);

interface Props extends Sizable, Themable {
  readonly backgroundImage?: SanityImageSource | undefined;
  readonly style?: CSSProperties;
  readonly fullscreen?: boolean | undefined;
  readonly fullWidth?: boolean | undefined;
  readonly wide?: boolean | undefined;
}

function ComponentBlock({
  children,
  size = 'medium',
  backgroundImage,
  style,
  theme,
  fullscreen = false,
  fullWidth = false,
  wide = false,
}: PropsWithChildren<Props>): ReactElement {
  const { rootClass } = useComponentBlock({ theme });

  const resolvedSize: ComponentSizes =
    typeof backgroundImage !== 'undefined' ? 'large' : size;

  const classes: string[] = [validateString(styles[`size-${resolvedSize}`])];

  return (
    <div
      className={classNames(rootClass, classes, {
        [fullscreenClass]: fullscreen,
      })}
      style={{
        ...style,
      }}
    >
      {typeof backgroundImage !== 'undefined' && (
        <div className={backgroundImageClass}>
          <Img alt="" fill imageSource={backgroundImage} />
        </div>
      )}
      <Container className={contentClass} full={fullWidth} wide={wide}>
        {children}
      </Container>
    </div>
  );
}

export default ComponentBlock;
